/* <mw-contact>: contact page «Guidet» (sections/main-contact.liquid).
   One question at a time: subject → «Måske er dette svaret» (only when the subject has answers)
   → details → receipt. The steps are fieldsets in one contact form; the server marks the first
   one .is-current and CSS (html.js) hides the rest. Here: choosing a subject moves on, each step
   is a history entry (#trin-2), so the browser's back button steps back, focus goes to the new
   step's heading, and the change slides (not under reduced motion).
   The subject decides the reply time, whether an order number is asked (never required), and which
   answers step 2 shows. Case number, what the receipt shows and the message draft live in
   sessionStorage (mw-contact, mw-contact:draft). Light DOM, no dependencies. */
(() => {
  if (customElements.get('mw-contact')) return;

  const KEY = 'mw-contact';
  const DRAFT = 'mw-contact:draft';
  const store = {
    get: (k) => {
      try {
        return sessionStorage.getItem(k);
      } catch (e) {
        return null;
      }
    },
    set: (k, v) => {
      try {
        sessionStorage.setItem(k, v);
      } catch (e) {}
    },
    del: (k) => {
      try {
        sessionStorage.removeItem(k);
      } catch (e) {}
    },
  };
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Case numbers: MW- and five characters without look-alikes (no 0/O, 1/I). 32 characters, so
  // a random byte maps evenly. Made at submit, kept through a server error.
  const CHARS = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
  const newTicket = () => 'MW-' + [...crypto.getRandomValues(new Uint8Array(5))].map((b) => CHARS[b % 32]).join('');

  // Typo check on the mail domain — same list and rule as assets/support.js. Only a suggestion.
  const DOMAINS = [
    'gmail.com', 'hotmail.com', 'hotmail.dk', 'hotmail.no', 'outlook.com', 'outlook.dk', 'outlook.no',
    'live.dk', 'live.com', 'live.no', 'msn.com', 'yahoo.com', 'yahoo.dk', 'yahoo.no', 'icloud.com',
    'me.com', 'mac.com', 'mail.dk', 'email.dk', 'jubii.dk', 'stofanet.dk', 'youmail.dk', 'privat.dk',
    'protonmail.com', 'proton.me', 'gmx.com', 'gmx.net', 'aol.com', 'online.no', 'getmail.no',
  ];
  const distance = (a, b) => {
    const d = Array.from({ length: a.length + 1 }, (_, i) => [i]);
    for (let j = 1; j <= b.length; j++) d[0][j] = j;
    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
    return d[a.length][b.length];
  };
  const suggestMail = (value) => {
    const m = /^([^@\s]+)@([^@\s]+)$/.exec(value.trim());
    if (!m) return '';
    const domain = m[2].toLowerCase();
    if (DOMAINS.includes(domain)) return '';
    let best = '';
    let bestD = 3;
    DOMAINS.forEach((dom) => {
      const dd = distance(domain, dom);
      if (dd < bestD) [best, bestD] = [dom, dd];
    });
    return best && bestD <= (domain.length > 6 ? 2 : 1) ? `${m[1]}@${best}` : '';
  };

  // Steps in reading order; «solved» sits where step 3 would.
  const RANK = { 1: 1, 2: 2, 3: 3, solved: 3 };
  const hashFor = (step) => '#trin-' + (step === 'solved' ? 'klar' : step);

  class MwContact extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;
      this.form = this.querySelector('form');

      let kept = null;
      try {
        kept = JSON.parse(store.get(KEY) || 'null');
      } catch (e) {}

      if (this.querySelector('[data-done]')) {
        store.del(DRAFT);
        if (kept) this.receipt(kept);
        this.wireCopy();
        requestAnimationFrame(() => this.querySelector('[data-done-title]')?.focus());
        return;
      }
      if (!this.form) return;

      const q = (s) => this.querySelector(s);
      this.stage = q('[data-stage]');
      this.steps = {};
      this.querySelectorAll('[data-step]').forEach((el) => (this.steps[el.dataset.step] = el));
      this.radios = [...this.form.querySelectorAll('input[name="contact[Emne]"]')];
      // The step can't move on without a choice, so the browser need not ask for one — and a
      // required radio in a hidden step would block the send without saying why.
      this.radios.forEach((r) => (r.required = false));
      this.progressEl = q('[data-progress]');
      this.label = q('[data-progress-label]');
      this.segs = [...this.querySelectorAll('[data-seg]')];
      this.sla = q('[data-sla]');
      this.slaDefault = this.sla?.value || '';
      this.ticket = q('[data-ticket-input]');
      this.order = q('[data-order]');
      this.orderInput = this.order?.querySelector('input[name="contact[Ordrenummer]"]');
      this.email = this.form.querySelector('input[name="contact[email]"]');
      this.body = this.form.querySelector('textarea[name="contact[body]"]');
      this.submit = this.form.querySelector('[type="submit"]');
      this.depth = 0;

      const errors = q('[data-errors]');
      if (kept && errors) {
        const r = this.radios.find((x) => x.value === kept.topic);
        if (r) r.checked = true;
        if (this.orderInput && kept.order) this.orderInput.value = kept.order;
        if (this.ticket && kept.ticket) this.ticket.value = kept.ticket;
      }

      this.current = q('[data-step].is-current')?.dataset.step || this.sequence()[0];
      this.sync();

      // Back on #trin-2 or #trin-3 (a reload, or back from an article without the page cache):
      // go straight there if the choice survived; otherwise start clean.
      const m = /^#trin-(2|3)$/.exec(location.hash);
      if (!errors && m && this.checked() && this.sequence().includes(m[1])) this.show(m[1]);
      const stale = /^#trin-/.test(location.hash) && location.hash !== hashFor(this.current);
      history.replaceState({ ...(history.state || {}), cgb: this.current }, '', stale ? location.pathname + location.search : location.href);
      this.progress();

      this.wireTopics();
      this.wireButtons();
      this.wireFields();
      window.addEventListener('popstate', (e) => {
        const to = e.state?.cgb || this.sequence()[0];
        this.depth = Math.max(0, this.depth - 1);
        if (this.steps[to]) this.go(to, false);
      });
      window.addEventListener('pageshow', () => this.submit?.removeAttribute('aria-busy'));

      if (errors) requestAnimationFrame(() => errors.focus());
      this.dataset.ready = '';
    }

    checked() {
      return this.radios.find((r) => r.checked) || null;
    }

    sequence() {
      const r = this.checked();
      const seq = [];
      if (this.radios.length) seq.push('1');
      const answers = r ? r.dataset.hasAnswers === 'true' : this.radios.some((x) => x.dataset.hasAnswers === 'true');
      if (answers && this.steps['2']) seq.push('2');
      seq.push('3');
      return seq;
    }

    wireTopics() {
      this.radios.forEach((r) => {
        r.addEventListener('keydown', (e) => {
          // Arrow keys move the choice (and fire click); only Enter, Space or a click moves on.
          // Space is handled here too: Chrome does not click a radio that is already checked, so
          // after arrowing to a tile Space would do nothing.
          if (e.key.startsWith('Arrow')) {
            this.arrowed = true;
            setTimeout(() => (this.arrowed = false), 60);
          } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            r.checked = true;
            this.pick(r);
          }
        });
        r.addEventListener('click', () => !this.arrowed && this.pick(r));
        r.addEventListener('change', () => this.sync());
      });
    }

    pick(r) {
      if (this.moving) return;
      this.sync();
      const next = r.dataset.hasAnswers === 'true' && this.steps['2'] ? '2' : '3';
      clearTimeout(this.pickTimer);
      // A beat to see the tile take the choice before the step slides away.
      this.pickTimer = setTimeout(() => this.go(next), reduced() ? 0 : 180);
    }

    wireButtons() {
      this.addEventListener('click', (e) => {
        const b = e.target.closest('button');
        if (!b || !this.contains(b)) return;
        if (b.dataset.goto) this.go(b.dataset.goto);
        else if (b.hasAttribute('data-solved')) this.go('solved');
        else if (b.hasAttribute('data-restart')) this.go(this.sequence()[0]);
        else if (b.hasAttribute('data-back')) this.back();
        else if (b.dataset.orderPick && this.orderInput) {
          this.orderInput.value = b.dataset.orderPick;
          this.orderInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });
    }

    back() {
      if (this.depth > 0 && history.state?.cgb === this.current) {
        history.back();
        return;
      }
      const seq = this.sequence();
      const to = seq[Math.max(0, seq.indexOf(this.current) - 1)];
      this.go(to, false);
      history.replaceState({ ...(history.state || {}), cgb: to }, '', location.pathname + location.search + (to === seq[0] ? '' : hashFor(to)));
    }

    go(to, push = true) {
      if (!this.steps[to] || to === this.current) return;
      const from = this.current;
      const dir = RANK[to] >= RANK[from] ? 1 : -1;
      this.current = to;
      if (push) {
        history.pushState({ ...(history.state || {}), cgb: to }, '', hashFor(to));
        this.depth++;
      }
      this.progress();
      this.swap(this.steps[from], this.steps[to], dir);
    }

    // Instant switch, for arriving on a step without a transition.
    show(to) {
      Object.values(this.steps).forEach((s) => s.classList.toggle('is-current', s.dataset.step === to));
      this.current = to;
    }

    time(name, fallback) {
      const v = getComputedStyle(this).getPropertyValue(name).trim();
      const n = parseFloat(v);
      if (Number.isNaN(n)) return fallback;
      return /ms$/.test(v) ? n : n * 1000;
    }

    // Out (short, the way we came from), in (a little longer, from the way we go), while the
    // stage eases between the two heights so the page below does not jump. A newer swap cancels
    // an older one mid-way (fast back-button taps): the token makes the old one stand down.
    swap(a, b, dir) {
      const token = (this.token = (this.token || 0) + 1);
      this.stage.getAnimations?.({ subtree: true }).forEach((an) => an.cancel());
      this.stage.classList.remove('is-moving');
      const settle = () => Object.values(this.steps).forEach((s) => s.classList.toggle('is-current', s === b));
      const arrive = () => {
        const title = b.querySelector('[data-step-title]');
        title?.focus({ preventScroll: true });
        const root = getComputedStyle(document.documentElement);
        const top = this.progressEl?.getBoundingClientRect().top ?? 0;
        const header = parseFloat(root.getPropertyValue('--header-offset')) || 0;
        // The heading must also clear what covers the bottom: the dock and the step's own send
        // bar (on a short phone the bar alone can hide it, measured at 320×568).
        const bar = this.querySelector('[data-bottom-bar]');
        const barTop = bar && getComputedStyle(bar).position === 'sticky' ? bar.getBoundingClientRect().top : Infinity;
        const floor = Math.min(window.innerHeight - (parseFloat(root.getPropertyValue('--mw-dock-h')) || 0), barTop);
        const under = title && title.getBoundingClientRect().bottom > floor;
        if (top < header || under) this.progressEl.scrollIntoView({ block: 'start', behavior: reduced() ? 'auto' : 'smooth' });
      };
      // Floating things (the chat launcher) re-measure once the stage has its new height.
      const landed = () => document.dispatchEvent(new CustomEvent('mw:layout'));
      if (reduced() || !b.animate || !a.classList.contains('is-current')) {
        settle();
        arrive();
        this.moving = false;
        landed();
        return;
      }
      this.moving = true;
      const ease = getComputedStyle(this).getPropertyValue('--ease-out').trim() || 'ease-out';
      const h0 = this.stage.offsetHeight;
      const out = a.animate(
        [
          { opacity: 1, transform: 'none' },
          { opacity: 0, transform: `translateX(${-16 * dir}px)` },
        ],
        { duration: this.time('--dur-fast', 200) * 0.8, easing: 'ease-in' }
      );
      out.onfinish = () => {
        if (token !== this.token) return;
        settle();
        const h1 = this.stage.offsetHeight;
        arrive();
        const opts = { duration: this.time('--dur', 350), easing: ease };
        b.animate(
          [
            { opacity: 0, transform: `translateX(${24 * dir}px)` },
            { opacity: 1, transform: 'none' },
          ],
          opts
        );
        this.stage.classList.add('is-moving');
        const grow = this.stage.animate([{ height: `${h0}px` }, { height: `${h1}px` }], opts);
        grow.onfinish = () => {
          if (token !== this.token) return;
          this.stage.classList.remove('is-moving');
          this.moving = false;
          landed();
        };
      };
    }

    progress() {
      if (!this.progressEl || !this.label) return;
      const seq = this.sequence();
      const solved = this.current === 'solved';
      const n = solved ? seq.length : seq.indexOf(this.current) + 1;
      this.progressEl.hidden = solved;
      this.segs.forEach((s, i) => {
        s.hidden = i >= seq.length;
        s.classList.toggle('is-on', i < n);
      });
      const text = this.label.dataset.template.replace('%STEP%', n).replace('%TOTAL%', seq.length);
      if (this.label.textContent !== text) this.label.textContent = text;
    }

    // The subject decides: pills, hint, answers shown, order number, reply time.
    sync() {
      const r = this.checked();
      this.querySelectorAll('[data-topic-name]').forEach((el) => (el.textContent = r ? r.value : ''));
      this.querySelectorAll('[data-topic-pill]').forEach((el) => (el.hidden = !r));
      const hint = this.querySelector('[data-topic-hint]');
      if (hint) hint.textContent = r?.dataset.hint || hint.dataset.default;
      this.querySelectorAll('[data-answers-for]').forEach((g) => (g.hidden = !r || g.dataset.answersFor !== r.dataset.handle));

      // Asked for where it helps, never required: a customer who bought from a retailer or was
      // given the product has no order number.
      const needs = r?.dataset.needsOrder === 'true';
      if (this.order) this.order.hidden = !needs;
      const input = this.orderInput;
      if (input) {
        input.disabled = !needs;
        if (needs) {
          input.pattern = input.dataset.pattern;
          input.title = input.dataset.patternTitle;
        } else {
          input.removeAttribute('pattern');
          input.removeAttribute('title');
        }
      }

      const response = r?.dataset.response || '';
      if (this.sla) this.sla.value = response || this.slaDefault;
      const reply = this.querySelector('[data-reply]');
      if (reply) reply.textContent = response || reply.dataset.default;
      if (this.label) this.progress();
    }

    wireFields() {
      // Mail typo suggestion.
      const box = this.querySelector('[data-suggest]');
      if (box && this.email) {
        const text = box.querySelector('[data-suggest-text]');
        const check = () => {
          const before = this.suggested;
          this.suggested = suggestMail(this.email.value);
          box.hidden = !this.suggested;
          if (this.suggested) text.textContent = box.dataset.template.replace('%EMAIL%', this.suggested);
          if (this.suggested && this.suggested !== before) this.announce(text.textContent);
        };
        this.email.addEventListener('change', check);
        this.email.addEventListener('input', () => !box.hidden && check());
        box.querySelector('[data-suggest-apply]')?.addEventListener('click', () => {
          this.email.value = this.suggested;
          this.suggested = '';
          box.hidden = true;
          this.markEmail?.(true);
          this.email.focus();
        });
        if (this.email.value) check();
      }

      // Friendly checks (presentation only): a tick inside the field once the value looks right,
      // judged when the customer leaves the field; typing only ever takes a tick away.
      const tick = (input, ok) => input?.closest('[data-control]')?.classList.toggle('is-ok', ok);
      if (this.email) {
        const looksRight = () => /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(this.email.value.trim()) && !this.suggested;
        this.markEmail = (final) => tick(this.email, (final || this.email.closest('.is-ok')) && looksRight());
        this.email.addEventListener('input', () => this.markEmail(false));
        this.email.addEventListener('change', () => this.markEmail(true));
        if (this.email.value) this.markEmail(true);
      }

      // Order number: a tick when it is 3–8 digits (a leading # and spaces are tidied away), and
      // the hint turns into the format help when what was typed cannot be an order number.
      const hint = this.querySelector('[data-order-hint]');
      if (this.orderInput) {
        const ORDER = /^\s*#?\s*\d{3,8}\s*$/;
        const plain = hint?.textContent || '';
        const markOrder = (final) => {
          const v = this.orderInput.value;
          const ok = !this.orderInput.disabled && ORDER.test(v);
          tick(this.orderInput, ok);
          if (final && ok && /\D/.test(v)) this.orderInput.value = v.replace(/\D/g, '');
          if (!hint) return;
          const wrong = ok ? false : final ? v.trim() !== '' : hint.classList.contains('is-wrong') && v.trim() !== '';
          if (wrong === hint.classList.contains('is-wrong')) return;
          hint.classList.toggle('is-wrong', wrong);
          hint.textContent = wrong ? hint.dataset.format : plain;
        };
        this.orderInput.addEventListener('input', () => markOrder(false));
        this.orderInput.addEventListener('change', () => markOrder(true));
        if (this.orderInput.value) markOrder(true);
      }

      // A very short message gets a gentle nudge once the customer pauses (never while typing).
      const nudge = this.querySelector('[data-body-hint]');
      if (nudge && this.body) {
        let pause;
        const judge = () => {
          const n = this.body.value.trim().length;
          const show = n > 0 && n < 20;
          if (show && nudge.hidden) this.announce(nudge.textContent.trim());
          nudge.hidden = !show;
        };
        this.body.addEventListener('input', () => {
          clearTimeout(pause);
          if (this.body.value.trim().length >= 20) nudge.hidden = true;
          else pause = setTimeout(judge, 1200);
        });
        this.body.addEventListener('blur', () => {
          clearTimeout(pause);
          judge();
        });
      }

      // Logged-in customer: the picked order is pressed while the field holds its number.
      const picks = [...this.querySelectorAll('[data-order-pick]')];
      if (picks.length && this.orderInput) {
        const mark = () => {
          const v = this.orderInput.value.replace(/\D/g, '');
          picks.forEach((p) => p.setAttribute('aria-pressed', String(v !== '' && p.dataset.orderPick === v)));
        };
        this.orderInput.addEventListener('input', mark);
        mark();
      }

      // The message is kept while it is written, so a mistap or reload does not lose it.
      if (this.body) {
        if (!this.body.value) this.body.value = store.get(DRAFT) || '';
        let t;
        this.body.addEventListener('input', () => {
          clearTimeout(t);
          t = setTimeout(() => store.set(DRAFT, this.body.value), 250);
        });
      }

      this.form.addEventListener('submit', (e) => {
        // A second tap while the first send is on its way would post the form twice.
        if (this.submit?.getAttribute('aria-busy') === 'true') {
          e.preventDefault();
          return;
        }
        if (this.form.querySelector('[data-hp]')?.value) {
          e.preventDefault();
          return;
        }
        if (this.ticket && !this.ticket.value) this.ticket.value = newTicket();
        const order = this.orderInput && !this.orderInput.disabled ? this.orderInput.value.replace(/\D/g, '') : '';
        store.set(
          KEY,
          JSON.stringify({
            ticket: this.ticket?.value || '',
            topic: this.checked()?.value || '',
            order,
            email: this.email?.value.trim() || '',
            response: this.sla?.value || '',
          })
        );
        this.submit?.setAttribute('aria-busy', 'true');
      });
    }

    // One polite live region for hints that appear (the typo suggestion, the nudge): a region that
    // starts out hidden is not announced reliably, so the text is copied into one that never is.
    announce(message) {
      const region = this.querySelector('[data-announce]');
      if (!region || !message) return;
      region.textContent = '';
      requestAnimationFrame(() => (region.textContent = message));
    }

    // Receipt: fill in what JS knows (the server only knows the form was posted).
    receipt(kept) {
      const box = this.querySelector('[data-ticket]');
      if (box && kept.ticket) {
        const no = box.querySelector('[data-ticket-no]');
        no.textContent = kept.ticket;
        no.dataset.value = kept.ticket;
        // The number settles in one character at a time (CSS staggers .cgb__char); the text, and
        // what «Kopiér» copies, stay the same string.
        if (!reduced()) {
          // Screen readers get the number as one word; the animated characters are for the eye.
          const whole = document.createElement('span');
          whole.className = 'visually-hidden';
          whole.textContent = kept.ticket;
          const shown = document.createElement('span');
          shown.setAttribute('aria-hidden', 'true');
          shown.append(
            ...[...kept.ticket].map((ch, i) => {
              const c = document.createElement('span');
              c.className = 'cgb__char';
              c.style.setProperty('--cgb-c', i);
              c.textContent = ch;
              return c;
            })
          );
          no.replaceChildren(whole, shown);
        }
        const meta = box.querySelector('[data-ticket-meta]');
        const parts = [kept.topic, kept.order && `${meta.dataset.orderLabel} ${kept.order}`].filter(Boolean);
        if (meta && parts.length) {
          meta.textContent = parts.join(' · ');
          meta.hidden = false;
        }
        box.hidden = false;
      }
      const mail = this.querySelector('[data-mail-to]');
      if (mail && kept.email) {
        const [before, after = ''] = mail.dataset.template.split('%EMAIL%');
        const address = document.createElement('strong');
        address.textContent = kept.email;
        mail.replaceChildren(before, address, after);
        mail.hidden = false;
      }
      const reply = this.querySelector('[data-done-reply]');
      if (reply && kept.response) reply.textContent = kept.response;
    }

    wireCopy() {
      const btn = this.querySelector('[data-copy]');
      const no = this.querySelector('[data-ticket-no]');
      if (!btn || !no) return;
      const label = btn.querySelector('[data-copy-label]');
      const live = this.querySelector('[data-copy-live]');
      btn.addEventListener('click', async () => {
        const text = (no.dataset.value || no.textContent).trim();
        if (!text) return;
        try {
          await navigator.clipboard.writeText(text);
        } catch (e) {
          // No clipboard (insecure context, denied): select the number so the customer can copy it.
          const range = document.createRange();
          // Only the characters on screen, not the screen-reader copy beside them.
          range.selectNodeContents(no.querySelector('[aria-hidden="true"]') || no);
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
          return;
        }
        btn.dataset.copied = '';
        label.textContent = btn.dataset.done;
        if (live) live.textContent = btn.dataset.live;
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => {
          delete btn.dataset.copied;
          label.textContent = btn.dataset.idle;
          if (live) live.textContent = '';
        }, 2400);
      });
    }
  }

  customElements.define('mw-contact', MwContact);
})();
